import { addDays, addYears, isAfter, parseISO, set, startOfDay } from 'date-fns';

import { ITimezone, IWallClock } from '../../../types/timezone';
import { getWallClock, resolveWallClockInstant, timeOfDay, wallClockToUtc } from '../../../utils/date';

export const releaseDateToUtcIso = (wallClock: IWallClock, timezone: ITimezone): string =>
  wallClockToUtc(wallClock, timezone).toISOString();

export const utcIsoToWallClock = getWallClock;

export const resolveWallClockIso = (wallClock: IWallClock, timezone: ITimezone, currentIso: string | null): string =>
  resolveWallClockInstant(wallClock, timezone, currentIso).toISOString();

export const CLAIM_EXPIRATION_MIN_LEAD_MS = 60 * 60 * 1000;

export const isBeforeReleaseMin = (iso: string, minLeadMs: number, now: Date = new Date()): boolean =>
  parseISO(iso).getTime() < now.getTime() + minLeadMs;

export const isPastReleaseInstant = (iso: string, graceMs: number, now: Date = new Date()): boolean =>
  isBeforeReleaseMin(iso, -graceMs, now);

export const releaseDateInDays = (days: number): Date =>
  set(addDays(new Date(), days), { hours: 23, minutes: 55, seconds: 0, milliseconds: 0 });

export const dayToUtcMidnightIso = (day: Date): string =>
  new Date(Date.UTC(day.getFullYear(), day.getMonth(), day.getDate())).toISOString();

export const utcMidnightIsoToDay = (iso: string): Date => {
  const instant = parseISO(iso);

  return new Date(instant.getUTCFullYear(), instant.getUTCMonth(), instant.getUTCDate());
};

export const holdDayInDays = (days: number, now: Date = new Date()): Date =>
  new Date(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + days);

export const earliestHoldDay = (now: Date = new Date()): Date => holdDayInDays(1, now);

export const nowInTimezone = (timezone: ITimezone, now: Date = new Date()): IWallClock => getWallClock(now, timezone);

export const oneYearAhead = (now: Date = new Date()): Date => addYears(now, 1);

export const isBeyondMaxDay = (candidate: Date, maxSelectableDate?: Date | null): boolean =>
  Boolean(maxSelectableDate) && isAfter(startOfDay(candidate), startOfDay(maxSelectableDate as Date));

export const isWallClockDay = (day: Date, wallClock: IWallClock): boolean =>
  day.getFullYear() === wallClock.year && day.getMonth() + 1 === wallClock.month && day.getDate() === wallClock.day;

export const getReleaseMinTime = (
  day: Date | null,
  timezone: ITimezone,
  stepMinutes: number,
  now: Date = new Date(),
): Date | undefined => {
  const tzNow = nowInTimezone(timezone, now);
  if (day && !isWallClockDay(day, tzNow)) {
    return undefined;
  }
  const remainder = tzNow.minutes % stepMinutes;
  const minutes = remainder === 0 ? tzNow.minutes : tzNow.minutes + (stepMinutes - remainder);
  return timeOfDay(tzNow.hours, minutes);
};
